// src/pages/help/HelpArticle.tsx
// Vista de un artículo individual del Centro de Ayuda

import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { helpSections, HelpArticle as ArticleType, HelpSection } from '../../data/helpCenterData';
import { useUser } from '../../context/UserContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function HelpArticle() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();

  // Verificar permisos del usuario
  const isAdmin = user?.rol === 'ADMIN';
  const isAdminComite = user?.esAdminComite === true;

  const [article, setArticle] = useState<ArticleType | null>(null);
  const [section, setSection] = useState<HelpSection | null>(null);
  const [prevArticle, setPrevArticle] = useState<ArticleType | null>(null);
  const [nextArticle, setNextArticle] = useState<ArticleType | null>(null);

  // Verificar si el usuario tiene acceso a este artículo/sección
  const hasAccess = useMemo(() => {
    if (!section) return true; // Mientras carga, permitir

    // Sección de admin solo para admins
    if (section.id === 'admin-guide' && !isAdmin) {
      return false;
    }
    // Sección de comité solo para admins y admin de comité
    if (section.id === 'committee-guide' && !isAdmin && !isAdminComite) {
      return false;
    }
    return true;
  }, [section, isAdmin, isAdminComite]);

  // Filtrar artículos accesibles para navegación prev/next
  const filterAccessibleArticles = (articles: ArticleType[]) => {
    return articles.filter(a => {
      if (!a.role || a.role === 'all') return true;
      if (a.role === 'admin' && isAdmin) return true;
      if (a.role === 'comite' && (isAdmin || isAdminComite)) return true;
      // Para asesor y asociado, todos pueden ver (son miembros)
      if (a.role === 'asesor' || a.role === 'asociado') return true;
      return false;
    });
  };

  useEffect(() => {
    // Buscar el artículo en todas las secciones
    for (const sec of helpSections) {
      const articleIndex = sec.articles.findIndex(a => a.id === articleId);
      if (articleIndex !== -1) {
        setArticle(sec.articles[articleIndex]);
        setSection(sec);

        // Filtrar artículos accesibles para navegación
        const accessibleArticles = filterAccessibleArticles(sec.articles);
        const currentIndexInFiltered = accessibleArticles.findIndex(a => a.id === articleId);

        // Artículo anterior
        if (currentIndexInFiltered > 0) {
          setPrevArticle(accessibleArticles[currentIndexInFiltered - 1]);
        } else {
          setPrevArticle(null);
        }

        // Artículo siguiente
        if (currentIndexInFiltered < accessibleArticles.length - 1) {
          setNextArticle(accessibleArticles[currentIndexInFiltered + 1]);
        } else {
          setNextArticle(null);
        }

        break;
      }
    }
  }, [articleId, isAdmin, isAdminComite]);

  if (!article || !section) {
    return (
      <>
        <Navbar transparent={false} />
        <div className="container py-5">
          <div className="text-center">
            <i className="fa-solid fa-file-circle-question fa-4x text-warning mb-4"></i>
            <h2>Artículo no encontrado</h2>
            <p className="text-muted">El artículo que buscas no existe o fue movido.</p>
            <Link to="/ayuda" className="btn btn-primary">
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver al Centro de Ayuda
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Sin acceso a este artículo/sección
  if (!hasAccess) {
    return (
      <>
        <Navbar transparent={false} />
        <div className="container py-5">
          <div className="text-center">
            <i className="fa-solid fa-lock fa-4x text-danger mb-4"></i>
            <h2>Acceso Restringido</h2>
            <p className="text-muted">No tienes permisos para ver este artículo del manual.</p>
            <p className="text-muted small">
              {section.id === 'admin-guide' && 'Este contenido está disponible solo para administradores.'}
              {section.id === 'committee-guide' && 'Este contenido está disponible solo para administradores y miembros del comité de eventos.'}
            </p>
            <Link to="/ayuda" className="btn btn-primary">
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver al Centro de Ayuda
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Filtrar artículos accesibles de la sección actual
  const accessibleSectionArticles = filterAccessibleArticles(section.articles);

  // Obtener artículos relacionados (misma sección, excluyendo el actual, solo accesibles)
  const relatedArticles = accessibleSectionArticles
    .filter(a => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar transparent={false} />

      {/* Header del artículo */}
      <section className="py-4 bg-light border-bottom">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Centro de Ayuda', path: '/ayuda' },
              { label: section.title, path: `/ayuda/seccion/${section.id}` },
              { label: article.title }
            ]}
          />
        </div>
      </section>

      {/* Contenido del artículo */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-lg-3 mb-4 mb-lg-0 order-lg-2">
              <div className="sticky-top" style={{ top: '100px' }}>
                {/* Tabla de contenido */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white border-0">
                    <h6 className="mb-0">
                      <i className="fa-solid fa-list-ol me-2"></i>
                      En esta sección
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush">
                      {accessibleSectionArticles.map((a) => (
                        <Link
                          key={a.id}
                          to={`/ayuda/articulo/${a.id}`}
                          className={`list-group-item list-group-item-action small ${a.id === article.id ? 'active' : ''}`}
                        >
                          {a.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {article.tags.length > 0 && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-0">
                      <h6 className="mb-0">
                        <i className="fa-solid fa-tags me-2"></i>
                        Etiquetas
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-wrap gap-2">
                        {article.tags.map((tag, i) => (
                          <span key={i} className="badge bg-light text-dark border">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contenido principal */}
            <div className="col-lg-9 order-lg-1">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4 p-lg-5">
                  {/* Título */}
                  <div className="d-flex align-items-center mb-4">
                    <span className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle me-3" style={{ width: '50px', height: '50px' }}>
                      <i className={`${section.icon} text-primary`}></i>
                    </span>
                    <div>
                      <Link to={`/ayuda/seccion/${section.id}`} className="text-muted small text-decoration-none">
                        {section.title}
                      </Link>
                      <h1 className="h3 mb-0">{article.title}</h1>
                    </div>
                  </div>

                  {/* Badge de rol si aplica */}
                  {article.role && article.role !== 'all' && (
                    <div className="alert alert-info d-flex align-items-center mb-4">
                      <i className="fa-solid fa-user-tag me-2"></i>
                      <span>
                        Este artículo es para usuarios con rol de <strong>
                          {article.role === 'admin' && 'Administrador'}
                          {article.role === 'comite' && 'Comité de Eventos'}
                          {article.role === 'asesor' && 'Asesor (Afiliado)'}
                          {article.role === 'asociado' && 'Asociado'}
                        </strong>
                      </span>
                    </div>
                  )}

                  {/* Contenido Markdown */}
                  <div className="help-article-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {article.content}
                    </ReactMarkdown>
                  </div>

                  {/* Feedback */}
                  <div className="border-top pt-4 mt-4">
                    <p className="text-muted mb-3">¿Te fue útil este artículo?</p>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-success btn-sm">
                        <i className="fa-solid fa-thumbs-up me-1"></i> Sí, me ayudó
                      </button>
                      <button className="btn btn-outline-secondary btn-sm">
                        <i className="fa-solid fa-thumbs-down me-1"></i> No me ayudó
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navegación entre artículos */}
              <div className="row mt-4 g-3">
                <div className="col-6">
                  {prevArticle && (
                    <Link to={`/ayuda/articulo/${prevArticle.id}`} className="text-decoration-none">
                      <div className="card border-0 shadow-sm h-100 hover-shadow">
                        <div className="card-body">
                          <span className="text-muted small">
                            <i className="fa-solid fa-arrow-left me-1"></i> Anterior
                          </span>
                          <h6 className="mb-0 text-dark mt-1">{prevArticle.title}</h6>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
                <div className="col-6 text-end">
                  {nextArticle && (
                    <Link to={`/ayuda/articulo/${nextArticle.id}`} className="text-decoration-none">
                      <div className="card border-0 shadow-sm h-100 hover-shadow">
                        <div className="card-body">
                          <span className="text-muted small">
                            Siguiente <i className="fa-solid fa-arrow-right ms-1"></i>
                          </span>
                          <h6 className="mb-0 text-dark mt-1">{nextArticle.title}</h6>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Artículos relacionados */}
              {relatedArticles.length > 0 && (
                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-header bg-white border-0">
                    <h5 className="mb-0">
                      <i className="fa-solid fa-book-open me-2"></i>
                      Artículos relacionados
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {relatedArticles.map((related) => (
                        <div className="col-md-4" key={related.id}>
                          <Link to={`/ayuda/articulo/${related.id}`} className="text-decoration-none">
                            <div className="p-3 border rounded hover-bg-light h-100">
                              <h6 className="text-dark mb-2">{related.title}</h6>
                              <span className="text-primary small">
                                Leer <i className="fa-solid fa-arrow-right ms-1"></i>
                              </span>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      {/* Estilos para el contenido markdown */}
      <style>{`
        .help-article-content h2 {
          font-size: 1.5rem;
          margin-top: 2rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #dee2e6;
        }
        .help-article-content h3 {
          font-size: 1.25rem;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .help-article-content h4 {
          font-size: 1.1rem;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .help-article-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        .help-article-content ul, .help-article-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .help-article-content li {
          margin-bottom: 0.5rem;
        }
        .help-article-content table {
          width: 100%;
          margin-bottom: 1rem;
          border-collapse: collapse;
        }
        .help-article-content th, .help-article-content td {
          padding: 0.75rem;
          border: 1px solid #dee2e6;
        }
        .help-article-content th {
          background-color: #f8f9fa;
          font-weight: 600;
        }
        .help-article-content code {
          background-color: #f8f9fa;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
          color: #e83e8c;
        }
        .help-article-content pre {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
        }
        .help-article-content pre code {
          background: none;
          padding: 0;
          color: inherit;
        }
        .help-article-content blockquote {
          border-left: 4px solid #0d6efd;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6c757d;
        }
        .help-article-content strong {
          font-weight: 600;
        }
        .hover-shadow:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </>
  );
}
